import Avo from 'analytics/Avo';
import { CallWashmenIcon, ChatWithWashmenIcon, RightArrowIcon, WhatsappWashmenIcon } from 'assets/icons/';
import cx from 'classnames';
import Separator from 'components/molecules/Separator';
import { useConfig } from 'context/app-config';
import useMessaging from 'hooks/useMessaging';
import i18n from 'i18n/config';
import Page from 'layout/page';
import { useGlobalConfig } from 'pages/queries/global.queries';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Col from 'react-bootstrap/Col';
import ListGroup from 'react-bootstrap/ListGroup';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

interface Props {}

const StyledRightArrowIcon = styled(RightArrowIcon)`
  & > g {
    & > g {
      stroke: ${({ theme }) => theme.text?.success};
    }
  }
`;

const StyledH5 = styled.h5`
  margin-bottom: 0;
  & > a {
    color: ${({ theme }) => theme.text?.success};
  }
`;

const StyledP = styled.p`
  font-weight: 300;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey100};
`;

const StyledSupportOptionTitle = styled.h5`
  color: ${({ theme }) => theme.colors.darkMidnightBlue};
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 184.615% */
`;

// TODO: Move all contacts constants to strapi.
const SUPPORT_PHONE = '+97148132999';
const WHATSAPP_SUPPORT_URL = `https://wa.me/${SUPPORT_PHONE}?text=Hello%2C%20I%27m%20looking%20for%20assistance.%20Could%20you%20help%20me%3F`;
const Support: React.FC<Props> = () => {
  const { t } = useTranslation();
  const [isThemeUpdated, setIsThemeUpdated] = useState(false);
  const { show: handleIntercomRedirect, update: updateIntercomTheme } = useMessaging();
  const theme = useTheme();
  const config = useConfig();
  const location = useLocation();
  const { data: appConfig } = useGlobalConfig();

  const isRTL = i18n.dir() === 'rtl';
  const rightIconClasses = `margin-start-auto ${isRTL && 'transform-180'}`;

  const isShowWhatsAppSupport = useMemo(() => appConfig?.isShowWhatsAppSupportPWA === 'true', [appConfig?.isShowWhatsAppSupportPWA]);

  useEffect(() => {
    Avo.viewSupport({ screen: 'home' });

    // hide splash
    const splash = document.getElementById('splash-screen');
    if (splash) {
      splash.style.display = 'none';
    }
  }, []);

  const onClickGroupItem = useCallback(() => {
    Avo.supportChat();
    if (!isThemeUpdated) {
      updateIntercomTheme({
        background_color: theme.intercom?.backgroundColor,
        action_color: theme.intercom?.actionColor,
      });
      setIsThemeUpdated(true);
    }
    handleIntercomRedirect();
  }, [handleIntercomRedirect, isThemeUpdated, theme.intercom?.actionColor, theme.intercom?.backgroundColor, updateIntercomTheme]);

  const onClickSupportCall = useCallback(() => {
    Avo.supportCall();
  }, []);

  const onClickCall = useCallback(() => {
    Avo.supportCall();
    window.location.href = `tel:${SUPPORT_PHONE}`;
  }, []);

  const onClickSupportMessage = useCallback(() => {
    Avo.supportMessage();
  }, []);

  const goBackRoute = useMemo(() => (location.key === 'default' ? '/home' : -1), [location.key]);

  const onWhatsAppClicked = useCallback(() => {
    window.location.href = WHATSAPP_SUPPORT_URL;
  }, []);

  const supportOptions = useMemo(
    () => [
      {
        title: 'supportPage.callWashmen',
        onClick: onClickCall,
        SupportIcon: CallWashmenIcon,
        isEnabled: true,
      },
      {
        title: 'supportPage.whatsAppSupport',
        onClick: onWhatsAppClicked,
        SupportIcon: WhatsappWashmenIcon,
        isEnabled: isShowWhatsAppSupport,
      },
      {
        title: 'supportPage.chatWithWashmen',
        onClick: onClickGroupItem,
        SupportIcon: ChatWithWashmenIcon,
        isEnabled: true,
      },
    ],
    [onClickCall, onWhatsAppClicked, onClickGroupItem, isShowWhatsAppSupport]
  );

  const SupportOptions = useMemo(
    () =>
      supportOptions.map(({ title, SupportIcon, onClick, isEnabled }) =>
        isEnabled ? (
          <ListGroup className="mb-2" variant="flush" key={title}>
            <ListGroup.Item className="px-0 py-1 border-bottom-0" onClick={onClick}>
              <div className="d-flex align-items-center">
                <div className="icon">
                  <SupportIcon />
                </div>
                <div className="margin-start-4">
                  <StyledSupportOptionTitle>{t(title)}</StyledSupportOptionTitle>
                </div>
                <StyledRightArrowIcon className={rightIconClasses} />
              </div>
            </ListGroup.Item>
          </ListGroup>
        ) : null
      ),
    [supportOptions, t]
  );

  return (
    <Page
      headerProps={{
        isShowPricingOption: false,
        isShowSupportOption: true,
      }}
      showBackToHome
      hasBack
      route={goBackRoute}
      className="pl-16 pr-16 pb-9"
    >
      <Row>
        <Col xs={12} className={cx({ 'mt-3': config?.hideBackButton })}>
          <h2 className="text-dark-blue">{t('common.washmenSupport')}</h2>
        </Col>
        <Col>
          <StyledP className="font-weight-light text-gray-dark m-0">{t('supportPage.description')}</StyledP>
        </Col>
        <Col xs={12}>
          <Separator sm expandHorizontal={false} />
        </Col>
        <Col xs={12}>
          <StyledH5
            className="font-weight-light text-gray-dark"
            dangerouslySetInnerHTML={{ __html: t('supportPage.info1') }}
            onClick={onClickSupportCall}
          />
          <StyledH5
            className="font-weight-light text-gray-dark"
            dangerouslySetInnerHTML={{ __html: t('supportPage.info2') }}
            onClick={onClickSupportMessage}
          />
        </Col>
        <Col xs={12}>
          <Separator sm expandHorizontal={false} />
        </Col>
        <Col xs={12}>{SupportOptions}</Col>
      </Row>
    </Page>
  );
};

export default Support;
